import { useMemo } from 'react';
import _keyBy from 'lodash/keyBy';

import { ContextualUserGroupType } from '@/components/Form/components/VisibilitySettings/types';
import useGroups from '@/modules/corporate/modules/group/views/GroupManager/hooks/useGroups';
import { GroupStatusEnum, GroupTypeEnum } from '@/types/schema';

import { RestrictedVisibilitySettingsSelectedListProps } from '../components/RestrictedVisibilitySettingsSelectedList/types';
import { UseVisibilitySettingsReturnType } from './useVisibilitySettings';

const useVisibilitySettingsGroups = (
  props: Pick<UseVisibilitySettingsReturnType, 'values'> &
    Pick<RestrictedVisibilitySettingsSelectedListProps, 'contextualGroupsConfig'>,
) => {
  const {
    values: { groupIds, contextualGroups },
    contextualGroupsConfig,
  } = props;

  const { groups: initialGroups } = useGroups({
    filters: {
      ids: groupIds,
      systemGenerated: false,
      status: [GroupStatusEnum.Published],
      types: [GroupTypeEnum.UserCollection],
    },
    pause: !groupIds.length,
  });

  const _selectedContextualGroups: ContextualUserGroupType[] = useMemo(() => {
    const supportContextualGroupsMap = _keyBy(
      contextualGroupsConfig?.supportedContextualGroups,
      'id',
    );

    return (
      contextualGroups?.reduce<ContextualUserGroupType[]>((prev, curr) => {
        const contextualGroup = supportContextualGroupsMap[curr];
        if (contextualGroup) {
          return [
            ...prev,
            {
              ...contextualGroup,
              type: 'ContextualUserGroupType',
            } as ContextualUserGroupType,
          ];
        }

        return prev;
      }, []) ?? []
    );
  }, [contextualGroups, contextualGroupsConfig?.supportedContextualGroups]);

  const resolvedGroups = useMemo(() => {
    const groups = groupIds.length ? initialGroups : [];

    return groupIds.length || contextualGroups?.length
      ? [..._selectedContextualGroups, ...groups]
      : [];
  }, [_selectedContextualGroups, groupIds.length, initialGroups, contextualGroups?.length]);

  return { groups: resolvedGroups };
};

export default useVisibilitySettingsGroups;
